import React, { useEffect, useMemo, useRef } from 'react';
import { Marker } from 'react-leaflet';
import L from 'leaflet';
import { shipmentPinLatLng } from './portCoords';
import { shipmentHeading } from '../../../utils/shipHeading';
import { cargoIsFullyDischarged } from '../../../utils/shipChanges';
import { isHubWaiting, currentVesselName } from '../../../utils/hubWait';

const COMMODITY_COLORS = {
  'Grapes': '#7c3aed',
  'Blueberries': '#3b82f6',
  'Avocados': '#16a34a',
  'Citrus': '#f97316',
  'Mangoes': '#eab308',
  'Cherries': '#dc2626',
  'Asparagus': '#059669',
  'Pomegranates': '#be185d',
};

const DELAY_RED = '#dc2626';
const DELAY_STROKE = '#7f1d1d';
const AHEAD_GREEN = '#16a34a';
const AHEAD_STROKE = '#14532d';

const getCommodityColor = (commodities) => {
  if (!commodities) return '#22c55e';
  const first = commodities.split(',')[0].trim();
  for (const [key, color] of Object.entries(COMMODITY_COLORS)) {
    if (first.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return '#22c55e';
};


function coerceDelay(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') {
    if (value.value != null) return coerceDelay(value.value);
    return null;
  }
  const n = Number(String(value).trim().replace(/[^+0-9.\-]/g, ''));
  return Number.isNaN(n) ? null : n;
}


export { isHubWaiting, currentVesselName };

export function nextVesselName(shipment) {
  const next = shipment?.next_vessel;
  if (next == null || next === '') return '';
  const cur = currentVesselName(shipment);
  if (cur && String(next) === String(cur)) return '';
  return String(next);
}

/** Live ShipsGo ship when it differs from API vessel (booking after BE stop-stamping). */
export function liveVesselName(shipment) {
  const live = shipment?.current_vessel;
  if (live == null || live === '') return '';
  const booked = shipment?.vessel;
  if (booked && String(live) === String(booked)) return '';
  return String(live);
}

/** API voyage_stops only. Keep this row's arrival_port (or same shipsgo_shipment_id). Never merge other PODs by IMO. */
export function trustedVoyageStops(shipment) {
  const stops = asVoyageStops(shipment?.voyage_stops);
  const pods = [...new Set(stops.map((s) => String(s?.arrival_port || '').toUpperCase().trim()).filter(Boolean))];
  if (pods.length > 1) return stops;
  const pod = String(shipment?.arrival_port || '').toUpperCase().trim();
  if (!pod) return stops.slice(0, 1);
  const samePod = stops.filter((s) => String(s?.arrival_port || '').toUpperCase().trim() === pod);
  if (samePod.length) return samePod;
  const sid = shipment?.shipsgo_shipment_id;
  if (sid != null && sid !== '') {
    const sameId = stops.filter((s) => String(s?.shipsgo_shipment_id || '') === String(sid));
    if (sameId.length) return sameId;
  }
  return stops.slice(0, 1);
}

/** Render-only: parse API voyage_stops (array or JSON string). Never assemble from siblings. */
export function asVoyageStops(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function delayPool(shipment) {
  const stops = asVoyageStops(shipment?.voyage_stops);
  const groups = Array.isArray(shipment?.tracked_groups) ? shipment.tracked_groups : [];
  const rows = groups.length ? groups : stops;
  if (!rows.length) {
    const d = coerceDelay(shipment?.delay_days ?? shipment?.delayDays);
    return d != null ? [d] : [];
  }
  // DISCHARGED lots drop out of hull color. ARRIVED/at_port still mix.
  // delayed+on-time → delayed; ahead+on-time → ahead; delayed+ahead → delayed.
  const remaining = rows.filter((r) => !cargoIsFullyDischarged(r));
  const pool = remaining.length ? remaining : [];
  return pool
    .map((r) => coerceDelay(r?.delay_days ?? r?.delayDays))
    .filter((n) => n != null);
}

/** Max delay of remaining (non-discharged) lots. */
export function maxVoyageDelay(shipment) {
  const nums = delayPool(shipment);
  if (!nums.length) return null;
  return Math.max(...nums);
}

/** Whole-vessel delayed only from cargo still aboard. */
export function voyageHasDelay(shipment) {
  return delayPool(shipment).some((n) => n >= 1);
}

/** Whole-vessel ahead only from cargo still aboard, and none of those delayed. */
export function voyageIsAhead(shipment) {
  const nums = delayPool(shipment);
  if (!nums.length) return false;
  if (nums.some((n) => n >= 1)) return false;
  return nums.some((n) => n <= -1);
}

const createShipIcon = (rotation = 0, selected = false, delayed = false, ahead = false) => {
  // Same footprint as prod/dev (selected 58 / default 43). Hit-testing is ShipClickResolver, not icon size.
  const size = selected ? 58 : 43;
  const half = size / 2;
  const stroke = delayed ? DELAY_STROKE : ahead ? AHEAD_STROKE : '#5b9bd5';
  const fill = delayed ? DELAY_RED : ahead ? AHEAD_GREEN : '#ffffff';
  const ringColor = delayed ? DELAY_RED : ahead ? AHEAD_GREEN : '#16A34A';
  const deg = Number.isFinite(rotation) ? rotation : 0;
  const svg = selected
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 48 48">
        <g transform="rotate(${deg} 24 24)">
          <circle cx="24" cy="24" r="22" fill="none" stroke="${ringColor}" stroke-width="3" opacity="0.8"/>
          <circle cx="24" cy="24" r="18" fill="none" stroke="${ringColor}" stroke-width="1.5" opacity="0.4"/>
          <path d="M24 12 L18 30 L24 26 L30 30 Z" fill="${fill}" stroke="${stroke}" stroke-width="2" stroke-linejoin="round"/>
        </g>
      </svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">
        <g transform="rotate(${deg} 12 12)">
          <path d="M12 2 L8 10 L12 8 L16 10 Z" fill="${fill}" stroke="${stroke}" stroke-width="1.5" stroke-linejoin="round"/>
        </g>
      </svg>`;
  return L.divIcon({
    html: svg,
    className: delayed
      ? `ship-marker ship-marker--delayed ship-marker--d${size}`
      : ahead
        ? `ship-marker ship-marker--ahead ship-marker--a${size}`
        : 'ship-marker',
    iconSize: [size, size],
    iconAnchor: [half, half],
    popupAnchor: [0, -half],
  });
};

const HUB_AMBER = '#F59E0B';

// Non-rotating box glyph, same footprint as the ship icon so hit-testing is unchanged.
const createHubIcon = (selected = false) => {
  const size = selected ? 58 : 43;
  const half = size / 2;
  // Selected glyph is drawn inside a 2.2x group, so strokes are thinner to keep a similar pixel weight.
  const glyph = (rectStroke, lineStroke) => `<rect x="7" y="8" width="10" height="9" rx="1" fill="${HUB_AMBER}" stroke="#92400e" stroke-width="${rectStroke}"/>
      <path d="M7 11.5 H17 M12 8 V17" stroke="#92400e" stroke-width="${lineStroke}" fill="none"/>`;
  const svg = selected
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 48 48">
        <circle cx="24" cy="24" r="22" fill="none" stroke="${HUB_AMBER}" stroke-width="3" opacity="0.8"/>
        <circle cx="24" cy="24" r="18" fill="none" stroke="${HUB_AMBER}" stroke-width="1.5" opacity="0.4"/>
        <g transform="translate(24 24) scale(2.2) translate(-12 -12.5)">${glyph(0.9, 0.75)}</g>
      </svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24">${glyph(1.2, 1)}</svg>`;
  return L.divIcon({
    html: svg,
    className: 'ship-marker ship-marker--hub',
    iconSize: [size, size],
    iconAnchor: [half, half],
    popupAnchor: [0, -half],
  });
};

const DELAYED_Z = 1200000;
const AHEAD_Z = 400000;
const SELECTED_Z = 200000;

const ShipMarker = ({ shipment, onClick, isSelected = false, position = null, zIndexOffset = null, interactive = false }) => {
  const pin = position || shipmentPinLatLng(shipment);
  const latitude = pin?.[0];
  const longitude = pin?.[1];
  const hub = isHubWaiting(shipment);
  const delayed = !hub && voyageHasDelay(shipment);
  const ahead = !hub && !delayed && voyageIsAhead(shipment);
  const delayKey = maxVoyageDelay(shipment);
  const heading = useMemo(
    () => shipmentHeading(shipment, pin),
    [shipment, pin],
  );
  const icon = useMemo(
    () => (hub ? createHubIcon(isSelected) : createShipIcon(heading, isSelected, delayed, ahead)),
    [hub, heading, isSelected, delayed, ahead, delayKey],
  );
  const markerRef = useRef(null);

  useEffect(() => {
    const layer = markerRef.current;
    if (layer && icon && typeof layer.setIcon === 'function') {
      layer.setIcon(icon);
    }
    if (layer && delayed && typeof layer.bringToFront === 'function') {
      layer.bringToFront();
    }
  }, [icon, delayed]);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  return (
    <Marker
      ref={markerRef}
      position={[latitude, longitude]}
      icon={icon}
      title=""
      alt=""
      interactive={interactive}
      pane={delayed ? 'shipDelayed' : ahead ? 'shipAhead' : 'shipNormal'}
      zIndexOffset={zIndexOffset != null ? zIndexOffset : (delayed ? DELAYED_Z : ahead ? AHEAD_Z : isSelected ? SELECTED_Z : 0)}
      eventHandlers={interactive ? {
        click: () => onClick && onClick(shipment),
        touchend: (e) => {
          const touch = e.originalEvent?.changedTouches?.[0];
          const startPos = e.target._touchStartPos;
          if (startPos && touch) {
            const dx = Math.abs(touch.clientX - startPos.x);
            const dy = Math.abs(touch.clientY - startPos.y);
            if (dx < 10 && dy < 10) {
              e.originalEvent.preventDefault();
              onClick && onClick(shipment);
            }
          } else {
            onClick && onClick(shipment);
          }
          delete e.target._touchStartPos;
        },
        touchstart: (e) => {
          const touch = e.originalEvent?.touches?.[0];
          if (touch) {
            e.target._touchStartPos = { x: touch.clientX, y: touch.clientY };
          }
        },
      } : undefined}
    />
  );
};

export { COMMODITY_COLORS, getCommodityColor };
export default ShipMarker;
